// Subtype → color mapping for calendar chips, table badges, and type indicators.
// Uses existing Tailwind color tokens from our design system.

export interface SubtypeColorSet {
  bg: string;       // background class (e.g. 'bg-matcha-100')
  fg: string;       // foreground text class
  dot: string;      // small dot/swatch class
  border: string;   // border class
}

const SUBTYPE_COLORS: Record<string, SubtypeColorSet> = {
  'Livestream':         { bg: 'bg-ube-100',          fg: 'text-ube-700',          dot: 'bg-ube-500',          border: 'border-ube-300' },
  'Workshop':           { bg: 'bg-matcha-100',       fg: 'text-matcha-700',       dot: 'bg-matcha-500',       border: 'border-matcha-300' },
  'Clay Club':          { bg: 'bg-blueberry-100',    fg: 'text-blueberry-700',    dot: 'bg-blueberry-500',    border: 'border-blueberry-300' },
  'Customer Dinner':    { bg: 'bg-pomegranate-100',  fg: 'text-pomegranate-700',  dot: 'bg-pomegranate-500',  border: 'border-pomegranate-300' },
  'Founder Dinner':     { bg: 'bg-dragonfruit-100',  fg: 'text-dragonfruit-700',  dot: 'bg-dragonfruit-500',  border: 'border-dragonfruit-300' },
  'Webinar':            { bg: 'bg-ube-100',          fg: 'text-ube-700',          dot: 'bg-ube-500',          border: 'border-ube-300' },
  'Roadshow':           { bg: 'bg-tangerine-100',    fg: 'text-tangerine-700',    dot: 'bg-tangerine-500',    border: 'border-tangerine-300' },
  'Flagship':           { bg: 'bg-lemon-100',        fg: 'text-lemon-700',        dot: 'bg-lemon-500',        border: 'border-lemon-300' },
  'In-Person':          { bg: 'bg-tangerine-100',    fg: 'text-tangerine-700',    dot: 'bg-tangerine-500',    border: 'border-tangerine-300' },
  'Bootcamp':           { bg: 'bg-dragonfruit-100',  fg: 'text-dragonfruit-700',  dot: 'bg-dragonfruit-500',  border: 'border-dragonfruit-300' },
  'Clay Hosted':        { bg: 'bg-nightshade-100',   fg: 'text-nightshade-700',   dot: 'bg-nightshade-500',   border: 'border-nightshade-300' },
  'Conference':         { bg: 'bg-lemon-100',        fg: 'text-lemon-700',        dot: 'bg-lemon-500',        border: 'border-lemon-300' },
  'Dinner':             { bg: 'bg-pomegranate-100',  fg: 'text-pomegranate-700',  dot: 'bg-pomegranate-500',  border: 'border-pomegranate-300' },
  'Executive Event':    { bg: 'bg-dragonfruit-100',  fg: 'text-dragonfruit-700',  dot: 'bg-dragonfruit-500',  border: 'border-dragonfruit-300' },
  'Group Demo':         { bg: 'bg-tangerine-100',    fg: 'text-tangerine-700',    dot: 'bg-tangerine-500',    border: 'border-tangerine-300' },
  'Partner Co-Branded': { bg: 'bg-blueberry-100',    fg: 'text-blueberry-700',    dot: 'bg-blueberry-500',    border: 'border-blueberry-300' },
  'Virtual':            { bg: 'bg-ube-100',          fg: 'text-ube-700',          dot: 'bg-ube-500',          border: 'border-ube-300' },
};

const DEFAULT_COLOR: SubtypeColorSet = {
  bg: 'bg-nightshade-100', fg: 'text-nightshade-700', dot: 'bg-nightshade-400', border: 'border-nightshade-300',
};

export function subtypeColor(subtype: string | null | undefined): SubtypeColorSet {
  if (!subtype) return DEFAULT_COLOR;
  return SUBTYPE_COLORS[subtype] ?? DEFAULT_COLOR;
}

// CM Status → colored pill classes
export interface StatusColorSet {
  bg: string;
  text: string;
}

const CM_STATUS_COLORS: Record<string, StatusColorSet> = {
  'Sent':       { bg: 'bg-blueberry-100',    text: 'text-blueberry-700' },
  'Registered': { bg: 'bg-matcha-100',        text: 'text-matcha-700' },
  'Attended':   { bg: 'bg-matcha-100',        text: 'text-matcha-700' },
  'No-Show':    { bg: 'bg-pomegranate-100',   text: 'text-pomegranate-700' },
  'Cancelled':  { bg: 'bg-nightshade-200',    text: 'text-nightshade-600' },
  'Invited':    { bg: 'bg-ube-100',           text: 'text-ube-700' },
  'Responded':  { bg: 'bg-blueberry-100',     text: 'text-blueberry-700' },
  'Declined':   { bg: 'bg-lemon-100',         text: 'text-lemon-700' },
  'Waitlisted': { bg: 'bg-tangerine-100',     text: 'text-tangerine-700' },
};

const DEFAULT_STATUS_COLOR: StatusColorSet = { bg: 'bg-nightshade-100', text: 'text-nightshade-600' };

export function cmStatusColor(status: string | null | undefined): StatusColorSet {
  if (!status) return DEFAULT_STATUS_COLOR;
  return CM_STATUS_COLORS[status] ?? DEFAULT_STATUS_COLOR;
}

// Launcher status pill
export function launcherStatusClasses(status: string): string {
  switch (status) {
    case 'launched': return 'bg-matcha-100 text-matcha-700';
    case 'draft':    return 'bg-lemon-100 text-lemon-700';
    default:         return 'bg-nightshade-100 text-nightshade-600';
  }
}

export function launcherStatusLabel(status: string): string {
  switch (status) {
    case 'launched': return 'Launched';
    case 'draft':    return 'Draft';
    default:         return status;
  }
}

// Stable color palette for per-campaign pill dots (indexed by position in campaign list)
const PILL_DOT_COLORS = [
  'bg-blueberry-500',
  'bg-ube-500',
  'bg-tangerine-500',
  'bg-dragonfruit-500',
  'bg-lemon-500',
  'bg-pomegranate-500',
  'bg-matcha-500',
  'bg-nightshade-500',
] as const;

export function pillDotColor(index: number): string {
  return PILL_DOT_COLORS[index % PILL_DOT_COLORS.length];
}
